import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useUser } from '../userContext';

const EditClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clientName, setClientName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { businessProfile, userProfile } = useUser();

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const clientDoc = await getDoc(doc(db, 'company', id));
        if (clientDoc.exists()) {
          setClientName(clientDoc.data().name);
        } else {
          setError('Client not found');
        }
      } catch (err) {
        setError('Error fetching client data');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClient();
  }, [id]);

  const isImpersonating = businessProfile?.id !== userProfile?.companyId;
  
  if (isImpersonating) {
    return (
      <div className="p-6 space-y-6 w-full" style={{fontFamily: '"Share Tech Mono", monospace'}}>
        <div className="w-full border-l-4 p-4 rounded-lg" style={{
          background: 'rgba(241, 196, 15, 0.2)',
          borderColor: '#F1C40F',
          color: '#F39C12'
        }} role="alert">
          <p className="font-bold">NOT APPLICABLE</p>
          <p>Client management is not available while impersonating a client.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await updateDoc(doc(db, 'company', id), { name: clientName });
      setSuccess(true);
      setTimeout(() => {
        navigate('/studio/clients');
      }, 1500);
    } catch (err) {
      setError('Error updating client');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="sm:px-4 pb-5 max-w-7xl mx-auto" style={{fontFamily: '"Share Tech Mono", monospace'}}>
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: '#F1C40F'}}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="sm:px-4 pb-5 max-w-7xl mx-auto" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      {/* Header */}
      <div className="mb-5 pb-3 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Link
            to="/studio/clients"
            className="inline-flex items-center justify-center p-2 rounded-lg transition-all duration-100"
            style={{
              background: 'rgba(52, 73, 94, 0.8)',
              border: '2px solid rgba(241, 196, 15, 0.3)',
              color: '#BDC3C7'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#F1C40F';
              e.target.style.color = '#F1C40F';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'rgba(241, 196, 15, 0.3)';
              e.target.style.color = '#BDC3C7';
            }}
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl md:text-3xl font-black tracking-wide" style={{
            color: '#F1C40F',
            textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
          }}>EDIT CLIENT</h1>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Main Form */}
        <div className="md:col-span-2">
          <div className="rounded-lg p-6" style={{
            background: 'linear-gradient(135deg, #2C3E50, #34495E)',
            border: '2px solid rgba(241, 196, 15, 0.4)'
          }}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="clientName" className="block text-sm font-bold mb-2" style={{color: '#BDC3C7'}}>
                  COMPANY NAME
                </label>
                <input
                  type="text"
                  id="clientName"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="block w-full rounded-lg py-2.5 px-3 text-sm font-bold transition-all duration-200"
                  style={{
                    background: 'rgba(52, 73, 94, 0.8)',
                    border: '2px solid rgba(241, 196, 15, 0.3)',
                    color: '#ECF0F1'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#F1C40F';
                    e.target.style.boxShadow = '0 0 8px rgba(241, 196, 15, 0.3)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(241, 196, 15, 0.3)';
                    e.target.style.boxShadow = 'none';
                  }}
                  required
                />
              </div>

              {error && (
                <div className="p-3 rounded-lg" style={{
                  background: 'rgba(231, 76, 60, 0.2)',
                  border: '1px solid rgba(231, 76, 60, 0.4)'
                }}>
                  <p className="text-sm font-bold" style={{color: '#E74C3C'}}>{error}</p>
                </div>
              )}

              {success && (
                <div className="p-3 rounded-lg" style={{
                  background: 'rgba(46, 204, 113, 0.2)',
                  border: '1px solid rgba(46, 204, 113, 0.4)'
                }}>
                  <p className="text-sm font-bold" style={{color: '#2ECC71'}}>CLIENT UPDATED SUCCESSFULLY!</p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <Link
                  to="/studio/clients"
                  className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold transition-all duration-100"
                  style={{
                    background: 'rgba(52, 73, 94, 0.8)',
                    border: '2px solid rgba(149, 165, 166, 0.4)',
                    color: '#BDC3C7'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#95A5A6';
                    e.target.style.color = '#ECF0F1';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = 'rgba(149, 165, 166, 0.4)';
                    e.target.style.color = '#BDC3C7';
                  }}
                >
                  CANCEL
                </Link>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold transition-all duration-100"
                  style={!isLoading ? {
                    background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                    border: '2px solid rgba(241, 196, 15, 0.6)',
                    color: '#FFFFFF',
                    textShadow: '1px 1px 0px #A93226'
                  } : {
                    background: 'rgba(52, 73, 94, 0.6)',
                    border: '1px solid rgba(149, 165, 166, 0.3)',
                    color: '#95A5A6',
                    cursor: 'not-allowed'
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) {
                      e.target.style.background = 'linear-gradient(135deg, #C0392B, #A93226)';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoading) {
                      e.target.style.background = 'linear-gradient(135deg, #E74C3C, #C0392B)';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      UPDATING...
                    </>
                  ) : (
                    'UPDATE CLIENT'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar - Could be used for additional options or information */}
        <div className="md:col-span-1">
          <div className="rounded-lg p-6" style={{
            background: 'linear-gradient(135deg, #2C3E50, #34495E)',
            border: '2px solid rgba(241, 196, 15, 0.4)'
          }}>
            <h2 className="text-lg font-black tracking-wide mb-4" style={{
              color: '#F1C40F',
              textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
            }}>CLIENT DETAILS</h2>
            <p className="text-sm font-bold" style={{color: '#BDC3C7'}}>
              Update your client's company information here. Changes will be reflected across all associated campaigns and reports.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditClient;
